<template>
  <div class="editor">
    <div v-if="serialNumber" style="width: 35px">{{ serialNumber }}. </div>
    <textarea ref="textarea" v-model="editorContent" :id="editorName"></textarea>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import contentCss from './content.css'
import { debounce } from 'lodash'
import { statsProcess } from '@/services/exam'
import { postProcessStats, parseStatsData } from '@/utils/stats'

const tinymce = window.tinymce

export default {
  name: 'Editor',
  props: {
    width: {
      type: Number,
      default: 200
    },
    height: {
      type: Number,
      default: 30
    },
    contentPadding: {
      type: Number,
      default: 0
    },
    lastAnswer: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    menubar: {
      type: Array,
      default: () => []
    },
    toolbar: {
      type: Array,
      default: () => []
    },
    autoresizeBottomMargin: {
      type: Number,
      default: 50
    },
    type: {
      type: String,
      default: 'text'
    },
    // 序号
    serialNumber: {
      type: Number,
      default: 0
    },
    // 语音录入
    voiceEntering: {
      type: [String, Object],
      default: ''
    },
    voiceEnteringIng: {
      type: Boolean,
      default: false
    },
    // 埋点相关数据
    dataStatsVar: {
      type: [String, Number],
      default: ''
    },
    dataInputStats: {
      type: String,
      default: ''
    },
    dataStatsNote: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      editorContent: ' ',
      editorName: uuidv4(),
      // 埋点统计
      eventTracking: debounce(statsProcess, 399),
      currentEditor: null
    }
  },
  watch: {
    lastAnswer: {
      handler(val) {
        if (this.lastAnswer) {
          this.editorContent = `<span style="color: #383838;">${this.lastAnswer}</span><span style="color: #ff0000">&nbsp;&nbsp;</span>`
          this.dispatchAnswer(this.editorContent)
        }
      },
      immediate: true
    },
    readonly: {
      handler(val) {
        this.$nextTick(() => {
          if (val) {
            setTimeout(() => {
              if (tinymce.get(this.editorName)) {
                tinymce.get(this.editorName).setMode('readonly')
              }
            }, 100)
          } else if (tinymce.get(this.editorName)) {
            tinymce.get(this.editorName).setMode('design')
          }
        })
      },
      immediate: true
    },
    voiceEntering: {
      handler(val) {
        if (val && this.voiceEnteringIng) {
          if (this.currentEditor) {
            this.currentEditor.setContent(val);
            this.dispatchAnswer(val)
          } else {
            console.error('未初始化当前编辑器');
          }
          // tinymce.get(this.editorName).setContent(val)
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initEditor()
    })
  },
  methods: {
    initEditor() {
      if (tinymce.get(this.editorName)) {
        // 销毁实例
        tinymce.get(this.editorName).remove();
      }
      tinymce.init({
        selector: `#${this.editorName}`,
        width: '100%',
        autoresize_bottom_margin: this.autoresizeBottomMargin,
        plugins: 'autoresize',
        placeholder: this.placeholder,
        menubar: this.menubar,
        toolbar: this.toolbar,
        statusbar: false,
        branding: false,
        setup: this.setup,
        content_css: contentCss,
        convert_urls: false,
        content_style: `body { color: ${this.lastAnswer && (this.type === 'text' || this.type === 'ordered_text') ? '#ff0000' : this.type === 'drag_text' ? '#ff0000' : '#383838'} ; margin: ${this.contentPadding}px; p { margin: 0px} }`,
        init_instance_callback: function(editor) {
          editor.on('input', function(e) {
            if (editor.getContent() !== '') {
              editor.getBody().style.color = ''; // 显示输入内容
            } else {
              editor.getBody().style.color = 'graytext'; // 显示占位符
            }
          });
        }
      })
    },
    setup(editor) {
      editor.on('init', () => {
        this.currentEditor = tinymce.get(this.editorName);

        // 视频题看完才可答题
        const editorIframe = editor.getDoc().querySelector('body');
        editorIframe.addEventListener('click', () => {
          if (this.readonly) {
            this.$message({
              message: '看完视频后开始答题',
              type: 'warning'
            })
          }
        });
        // 处理卡片拖拽类，不编辑直接提交显示样式问题
        let content = this.getContent();
        if (this.type !== 'text') {
          // 处理拖拽可编辑内容区域，回显问题
          if (this.lastAnswer) {
            content = content.replace('<p', '<span style="color: #ff0000;"').replace(/<\/p>/g, '</span>')
          }
          this.$emit('input', content, this.serialNumber - 1)
        }

        // 替换初始标签
        editor.formatter.register('paragraph', { inline: 'span' })

        // 将光标移动到文档末尾
        editor.selection.select(editor.getBody(), true);
        editor.selection.collapse(false);
      })
      editor.on('input', e => {
        let content = this.getContent();
        this.dispatchAnswer(content)
        // var cursorPosition = editor.selection.getRng().startOffset;
        // 单独添加数据埋点
        const value = this.dataInputStats
        const elData = typeof value === 'string' ? parseStatsData(value) : value
        delete elData.event2
        delete elData.note2
        // 提取纯文本内容
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        const plainText = tempDiv.textContent || tempDiv.innerText;
        const data = {
          topic: '',
          statsVar: this.dataStatsVar,
          ...elData,
          value: plainText
        }
        postProcessStats(data, 'input')
      })
      // 解决编辑器中不可编辑元素的问题
      editor.on('NodeChange', e => {
        var selection = editor.selection.getNode()
        if (selection && selection.getAttribute('contenteditable') === 'false') {
          editor.selection.collapse(false)
        }
      })
      // 禁止复制粘贴
      editor.on('Paste', e => {
        e.preventDefault()
      })
    },
    getContent() {
      return tinymce.get(this.editorName).getContent();
    },
    setContent(dom) {
      tinymce.get(this.editorName).setContent(dom)
    },
    setRecord(val) {
      this.currentEditor.setContent(val);
      this.dispatchAnswer(val)
    },
    /**
     * 发送答案
     *
     * @returns 无返回值
     */
    dispatchAnswer(content) {
      if (this.type !== 'text') {
        // 处理拖拽可编辑内容区域，回显问题
        if (this.lastAnswer) {
          content = content.replace('<p', '<span style="color: #ff0000;"').replace(/<\/p>/g, '</span>')
        }
        this.$emit('input', content, this.serialNumber - 1)
      } else {
        this.$emit('input', content)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.tox-tinymce {
  border-color: #e9e9e9;
  // margin-bottom: 50px;
}
.editor {
  display: flex !important;
  div {
    line-height: 25px;
  }
}
</style>
